import React, { useState, useEffect, useCallback, useMemo } from 'react';
import JobsAlertRecipients from './components/JobsAlertRecipients';
import { useAuth } from './AuthContext';

interface ProjectJobWatchesProps {
  projectId: number;
  projectName: string;
  onNavigateToCompany?: (companyName: string) => void;
  // Job Spec Match handoff: called with the freshly created spec id when the
  // user chooses to jump from a vacancy straight into candidate matching.
  onOpenJobSpecMatch?: (specId: number) => void;
}

interface JobKeywords {
  title?: string[];
  description?: string[];
  mode?: 'and' | 'or';
}

interface Location {
  country?: string;
  state?: string;
  city?: string;
}

interface Watch {
  scope_companies_total?: number | null;
  scope_sweep_days?: number | null;
  id: number;
  project_id: number;
  watch_name: string;
  job_keywords: JobKeywords | null;
  exclude_job_keywords: JobKeywords | null;
  departments: string[] | null;
  seniority_levels: string[] | null;
  job_types: string[] | null;
  industries: string[] | null;
  locations: Location[] | null;
  scope_to_project_companies: boolean;
  exclude_agency: boolean;
  results_per_run: number;
  is_recurring: boolean;
  recurring_frequency: string;
  recurring_enabled: boolean;
  company_employee_min: number | null;
  company_employee_max: number | null;
  company_countries: string[] | null;
  company_industries: string[] | null;
  company_tags: string[] | null;
  next_run_at: string | null;
  last_recurring_run_at: string | null;
  total_runs: number;
  status: string;
  jobs_count: number;
  total_credits_used: number;
  error_message: string | null;
  auto_discover_hms?: boolean;
  created_at: string;
}

// A hiring manager mapped at a job's company (GET /projects/:id/jobs → hms).
interface JobHm {
  id: number;
  name: string;
  job_title: string | null;
  li_profile_url: string | null;
  is_starred: boolean | null;
  discovery_kind: string | null;
  discovery_confidence: number | null;
  discovery_why: string | null;
}

// One hiring manager: name (→ LinkedIn) and title.
const HmLine: React.FC<{ hm: JobHm; full?: boolean }> = ({ hm, full }) => (
  <div className="leading-snug min-w-0">
    <div className="flex items-center gap-1 min-w-0">
      {hm.is_starred && <span className="text-amber-500 text-[11px]" title="Most likely the direct hiring manager">★</span>}
      {hm.li_profile_url
        ? <a href={hm.li_profile_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-medium text-gray-900 hover:text-blue-700 hover:underline truncate">{hm.name}</a>
        : <span className="font-medium text-gray-900 truncate">{hm.name}</span>}
      {hm.discovery_kind === 'talent_acquisition' && <span className="badge badge-gray !text-[9px] !px-1 shrink-0" title="Runs recruitment there">TA</span>}
      {full && hm.discovery_confidence != null && <span className="text-[10px] text-gray-400 shrink-0">{hm.discovery_confidence}%</span>}
    </div>
    {hm.job_title && <div className={`text-[11px] text-gray-500 ${full ? '' : 'truncate'}`}>{hm.job_title}</div>}
    {full && hm.discovery_why && <div className="text-[11px] text-gray-400 mt-0.5">{hm.discovery_why}</div>}
  </div>
);

interface Job {
  id: number;
  job_title: string;
  company_name: string;
  location: string;
  city: string | null;
  state: string | null;
  country: string | null;
  job_type: string | null;
  seniority_level: string | null;
  department: string | null;
  job_url: string;
  posted_date: string | null;
  first_seen_at: string;
  is_remote: boolean;
  job_board: string | null;
  job_description: string | null;
  matched_company_id: number | null;
  company_linkedin_url?: string | null;
  company_domain?: string | null;
  hm_count?: number;
  hm_discovered?: boolean;
  hms?: JobHm[] | null;
  matched_company_li?: string | null;
  watch_finds_hms?: boolean;
  is_new_employer: boolean;
  watch_name: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
}

interface ReferenceData {
  departments: string[];
  seniorityLevels: string[];
  jobTypes: string[];
  recurringFrequencies: { value: string; label: string }[];
}

const getToken = () => localStorage.getItem('token');
const authHeaders = (): HeadersInit => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

// Vacancies load this many at a time (the list used to stop at the newest 200).
const JOBS_BATCH = 1000;

const formatRelativeTime = (iso: string | null): string => {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatNextRun = (iso: string | null): string => {
  if (!iso) return 'Not scheduled';
  const date = new Date(iso);
  const now = new Date();
  if (date <= now) return 'Due now';
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `In ${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `In ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `In ${diffDays}d`;
};

const ProjectJobWatches: React.FC<ProjectJobWatchesProps> = ({ projectId, projectName, onNavigateToCompany, onOpenJobSpecMatch }) => {
  // Vacancy → Job Spec Match: create a spec from the job's description (AI
  // breakdown runs server-side, takes a few seconds), then offer to jump
  // straight into matching or stay browsing jobs (spec is saved either way).
  const [matchBusyJobId, setMatchBusyJobId] = useState<number | null>(null);
  const handleMatchCandidates = async (job: Job) => {
    if (matchBusyJobId) return;
    setMatchBusyJobId(job.id);
    try {
      const res = await fetch('/api/job-specs', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ projectId, jobId: job.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create the job spec');
      const goNow = window.confirm(
        `Job spec created for "${job.job_title}" at ${job.company_name}.\n\nOpen Job Spec Match to score candidates now? (Cancel keeps it saved for later — you'll find it under Job Spec Match in the Data Table.)`
      );
      if (goNow && onOpenJobSpecMatch) onOpenJobSpecMatch(data.spec.id);
    } catch (err: any) {
      window.alert(err.message || 'Failed to create the job spec');
    } finally {
      setMatchBusyJobId(null);
    }
  };
  const [available, setAvailable] = useState<boolean | null>(null);
  const [unavailableReason, setUnavailableReason] = useState<'not_configured' | 'upgrade_required' | 'no_jobs_quota' | 'error' | null>(null);
  const [unavailablePlanLabel, setUnavailablePlanLabel] = useState<string | null>(null);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  // Every job loads (in batches of JOBS_BATCH), not just the newest 200; the
  // archive is a second list you can look back at and restore from.
  const [jobsTotal, setJobsTotal] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reference, setReference] = useState<ReferenceData | null>(null);
  const [view, setView] = useState<'jobs' | 'watches'>('jobs');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationsFirstSetup, setNotificationsFirstSetup] = useState(false);
  const { user: authUser } = useAuth();
  const [editingWatch, setEditingWatch] = useState<Watch | null>(null);
  const [error, setError] = useState('');

  // Monthly job-vacancy usage (from /api/subscription/state). Used to render
  // the usage bar at the top of the tab and the "limit reached" banner.
  const [jobsUsage, setJobsUsage] = useState<{ used: number; allowance: number; periodEnd: string | null } | null>(null);

  useEffect(() => {
    fetch('/api/subscription/state', { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setJobsUsage({
          used: d.cycle?.jobsUsedThisCycle ?? 0,
          allowance: d.totalQuota?.jobs ?? 0,
          periodEnd: d.cycle?.periodEnd ?? null
        });
      })
      .catch(() => {});
  }, []);

  // Restore scroll position when the user returns to the Jobs tab
  // after clicking through to a company in the Companies tab.
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(`jobs-scroll-${projectId}`);
      if (saved) {
        // Defer until after render so the document has its full height.
        const y = parseInt(saved, 10);
        if (Number.isFinite(y)) {
          requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior }));
        }
        sessionStorage.removeItem(`jobs-scroll-${projectId}`);
      }
    } catch {}
  }, [projectId]);

  // Check Sentrion availability and load reference data
  useEffect(() => {
    fetch('/api/sentrion/status', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        setAvailable(d.available);
        setUnavailableReason(d.reason || null);
        setUnavailablePlanLabel(d.planLabel || null);
      })
      .catch(() => { setAvailable(false); setUnavailableReason('error'); });

    fetch('/api/sentrion/reference', { headers: authHeaders() })
      .then(r => r.json())
      .then(setReference)
      .catch(() => {});
  }, []);

  // Load watches and jobs
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [watchesRes, jobsRes] = await Promise.all([
        fetch(`/api/sentrion/projects/${projectId}/watches`, { headers: authHeaders() }),
        fetch(`/api/sentrion/projects/${projectId}/jobs?limit=${JOBS_BATCH}${showArchived ? '&archived=1' : ''}`, { headers: authHeaders() })
      ]);
      if (watchesRes.ok) {
        const data = await watchesRes.json();
        setWatches(data.watches || []);
      }
      if (jobsRes.ok) {
        const data = await jobsRes.json();
        setJobs(data.jobs || []);
        setJobsTotal(data.total ?? (data.jobs || []).length);
        setArchivedCount(data.archivedCount || 0);
      }
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }, [projectId, showArchived]);

  useEffect(() => {
    if (available) loadData();
  }, [available, loadData]);

  const loadMoreJobs = async () => {
    setLoadingMore(true);
    try {
      const r = await fetch(`/api/sentrion/projects/${projectId}/jobs?limit=${JOBS_BATCH}&offset=${jobs.length}${showArchived ? '&archived=1' : ''}`, { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setJobs((prev) => { const have = new Set(prev.map((j) => j.id)); return prev.concat((data.jobs || []).filter((j: Job) => !have.has(j.id))); });
        setJobsTotal(data.total ?? jobsTotal);
      }
    } catch { setError('Could not load more vacancies'); }
    setLoadingMore(false);
  };

  // Archive = off the list, kept (and restorable from the Archived view).
  const handleArchiveMany = async (ids: number[]) => {
    if (!ids.length) return;
    try {
      const r = await fetch(`/api/sentrion/projects/${projectId}/jobs/archive`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
      });
      if (!r.ok) throw new Error();
      const gone = new Set(ids);
      setJobs((prev) => prev.filter((j) => !gone.has(j.id)));
      setJobsTotal((t) => Math.max(0, t - ids.length));
      setArchivedCount((n) => n + ids.length);
    } catch { setError('Could not archive those vacancies'); }
  };
  const handleRestoreJob = async (jobId: number) => {
    try {
      const r = await fetch(`/api/sentrion/jobs/${jobId}/restore`, { method: 'POST', headers: authHeaders() });
      if (!r.ok) throw new Error();
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
      setJobsTotal((t) => Math.max(0, t - 1));
      setArchivedCount((n) => Math.max(0, n - 1));
    } catch { setError('Could not restore that vacancy'); }
  };

  const handleRunNow = async (watchId: number) => {
    try {
      const res = await fetch(`/api/sentrion/watches/${watchId}/run`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (res.ok) {
        // Refresh after a few seconds to allow background execution
        setTimeout(loadData, 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to trigger watch');
      }
    } catch (e) {
      setError('Failed to trigger watch');
    }
  };

  const handleToggleEnabled = async (watch: Watch) => {
    try {
      await fetch(`/api/sentrion/watches/${watch.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ recurringEnabled: !watch.recurring_enabled })
      });
      loadData();
    } catch (e) {
      setError('Failed to update watch');
    }
  };

  const handleDelete = async (watchId: number) => {
    if (!window.confirm('Delete this job watch? Jobs already collected will remain.')) return;
    try {
      await fetch(`/api/sentrion/watches/${watchId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      loadData();
    } catch (e) {
      setError('Failed to delete watch');
    }
  };

  const handleDismissJob = async (jobId: number) => {
    try {
      await fetch(`/api/sentrion/jobs/${jobId}/dismiss`, {
        method: 'POST',
        headers: authHeaders()
      });
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setJobsTotal((t) => Math.max(0, t - 1));
      setArchivedCount((n) => n + 1);
    } catch (e) {
      setError('Failed to archive job');
    }
  };

  // Loading / unavailable states
  if (available === null) {
    return (
      <div className="card p-8 text-center text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  if (!available) {
    // Tier-aware messaging:
    //   upgrade_required  → legacy user; show upgrade-to-Starter CTA
    //   no_jobs_quota     → tiered user with 0 jobs allowance; show buy-bolt-on CTA
    //   not_configured    → Sentrion API key missing at infra level
    //   error             → backend failure
    if (unavailableReason === 'upgrade_required') {
      return (
        <div className="card p-8">
          <div className="max-w-md mx-auto text-center">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">Upgrade to unlock job vacancy tracking</h3>
            <p className="text-sm text-gray-500 mb-4">
              You're on the <span className="font-medium text-gray-700">{unavailablePlanLabel || 'Legacy'}</span> plan, which doesn't include job tracking.
              Move to <span className="font-medium text-gray-700">Starter</span> or higher to start watching for new vacancies across the companies in your project.
            </p>
            <a
              href="/settings?tab=subscription"
              className="inline-block px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              View plans
            </a>
          </div>
        </div>
      );
    }
    if (unavailableReason === 'no_jobs_quota') {
      return (
        <div className="card p-8">
          <div className="max-w-md mx-auto text-center">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.5 0L3.16 16.25A2 2 0 005 19z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">Job tracking not included on your plan</h3>
            <p className="text-sm text-gray-500 mb-4">
              Your current plan doesn't include job vacancy tracking. Add a jobs bolt-on or upgrade to a tier that includes more.
            </p>
            <a
              href="/settings?tab=subscription"
              className="inline-block px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Manage subscription
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="card p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.5 0L3.16 16.25A2 2 0 005 19z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">Job Vacancy Tracking Not Configured</h3>
          <p className="text-sm text-gray-500">
            Job vacancy tracking isn't set up on this account yet. We'll notify you when available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Job Vacancies</h2>
          <p className="text-sm text-gray-500 mt-1">
            Track new job postings matching your criteria for <strong>{projectName}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNotifications(true)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center gap-1.5"
            title="Choose who receives the daily jobs email for this project"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Notifications
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Watch
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200/60 rounded-xl p-3 flex items-start justify-between">
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {/* Monthly job-vacancy usage. Renders only when the user has a non-zero
          allowance (paying tier or has bought jobs bolt-ons). */}
      {jobsUsage && jobsUsage.allowance > 0 && (() => {
        const pct = Math.min(100, Math.round((jobsUsage.used / jobsUsage.allowance) * 100));
        const capReached = jobsUsage.used >= jobsUsage.allowance;
        const nearCap = pct >= 80 && !capReached;
        const barColour = capReached ? 'bg-red-500' : nearCap ? 'bg-amber-500' : 'bg-blue-500';
        const periodEnd = jobsUsage.periodEnd
          ? new Date(jobsUsage.periodEnd).toLocaleDateString('en-GB')
          : null;
        return (
          <div className={`rounded-xl border p-4 ${capReached ? 'bg-red-50 border-red-200' : nearCap ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className={`font-medium ${capReached ? 'text-red-900' : nearCap ? 'text-amber-900' : 'text-blue-900'}`}>
                {capReached
                  ? 'Monthly job-vacancy limit reached'
                  : nearCap
                  ? 'Approaching your monthly job-vacancy limit'
                  : 'Job vacancies tracked this billing period'}
              </span>
              <span className="text-gray-600">
                {jobsUsage.used.toLocaleString('en-GB')} / {jobsUsage.allowance.toLocaleString('en-GB')}
              </span>
            </div>
            <div className="w-full bg-white/70 rounded-full h-2 mb-2">
              <div className={`h-2 rounded-full ${barColour}`} style={{ width: `${pct}%` }} />
            </div>
            {capReached && (
              <p className="text-xs text-red-800">
                Watches are paused until {periodEnd ? `${periodEnd}` : 'your next billing period'}.
                {' '}<a href="/settings?tab=subscription" className="underline font-medium">Add a +500 vacancies add-on</a>
                {' '}to keep tracking immediately.
              </p>
            )}
            {nearCap && (
              <p className="text-xs text-amber-800">
                Watches will pause once the limit is reached.
                {' '}<a href="/settings?tab=subscription" className="underline font-medium">Add a +500 vacancies add-on</a>
                {' '}to extend this cycle.
              </p>
            )}
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200/80">
        <button
          onClick={() => setView('jobs')}
          className={`px-4 py-2 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
            view === 'jobs'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Jobs ({jobsTotal.toLocaleString()}{showArchived ? ' archived' : ''})
        </button>
        <button
          onClick={() => setView('watches')}
          className={`px-4 py-2 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
            view === 'watches'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Watches ({watches.length})
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600/20 border-t-blue-600 mx-auto"></div>
        </div>
      )}

      {/* Empty state - no watches */}
      {!loading && watches.length === 0 && (
        <div className="card p-12 text-center animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">No job watches yet</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Create a watch to automatically track new job postings matching your criteria — by role, location, seniority, and more.
          </p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            Create your first watch
          </button>
        </div>
      )}

      {/* Archived view switch */}
      {!loading && view === 'jobs' && (archivedCount > 0 || showArchived) && (
        <div className="flex items-center justify-end -mb-2">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="text-xs font-medium text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline"
          >
            {showArchived ? '← Back to vacancies' : `Archived (${archivedCount.toLocaleString()})`}
          </button>
        </div>
      )}
      {!loading && view === 'jobs' && showArchived && jobs.length === 0 && (
        <div className="card p-8 text-center text-sm text-gray-500">Nothing archived.</div>
      )}

      {/* Jobs table */}
      {!loading && view === 'jobs' && jobs.length > 0 && (
        <JobsTable
          jobs={jobs}
          total={jobsTotal}
          archivedView={showArchived}
          onLoadMore={jobs.length < jobsTotal ? loadMoreJobs : undefined}
          loadingMore={loadingMore}
          onArchiveMany={handleArchiveMany}
          onRestore={handleRestoreJob}
          projectId={projectId}
          projectName={projectName}
          onDismiss={handleDismissJob}
          onNavigateToCompany={onNavigateToCompany}
          onMatchCandidates={onOpenJobSpecMatch ? handleMatchCandidates : undefined}
          matchBusyJobId={matchBusyJobId}
        />
      )}

      {/* Empty jobs (have watches but no jobs yet) */}
      {!loading && view === 'jobs' && !showArchived && watches.length > 0 && jobs.length === 0 && (
        <div className="card p-12 text-center animate-fade-in">
          <p className="text-sm text-gray-500">
            No job postings yet. Watches will populate as they run.
          </p>
        </div>
      )}

      {/* Watches list */}
      {!loading && view === 'watches' && watches.length > 0 && (
        <div className="space-y-3 animate-fade-in">
          {watches.map(watch => (
            <WatchCard
              key={watch.id}
              watch={watch}
              onRunNow={() => handleRunNow(watch.id)}
              onToggleEnabled={() => handleToggleEnabled(watch)}
              onEdit={() => setEditingWatch(watch)}
              onDelete={() => handleDelete(watch.id)}
            />
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {(showCreateModal || editingWatch) && reference && (
        <WatchEditorModal
          projectId={projectId}
          watch={editingWatch}
          reference={reference}
          onClose={() => { setShowCreateModal(false); setEditingWatch(null); }}
          onSaved={() => {
            // First watch on the project: jump straight to alert recipients —
            // a watch without anyone receiving its alerts is a silent watch.
            const firstWatch = watches.length === 0 && !editingWatch;
            setShowCreateModal(false); setEditingWatch(null); loadData();
            if (firstWatch) { setNotificationsFirstSetup(true); setShowNotifications(true); }
          }}
        />
      )}

      {/* Job alert recipients — jobs-only (no monitoring settings), so
          jobs-only clients can set up the daily email right here. */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowNotifications(false); setNotificationsFirstSetup(false); }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-gray-900 font-semibold">Job alert recipients</h3>
                <p className="text-xs text-gray-500 mt-0.5">{projectName}</p>
              </div>
              <button onClick={() => { setShowNotifications(false); setNotificationsFirstSetup(false); }} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="px-5 py-4">
              <JobsAlertRecipients
                projectId={projectId}
                autoEnableSelf={notificationsFirstSetup}
                currentUserId={authUser?.id ?? null}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Job description formatter
// =====================================================================
const SECTION_PATTERNS = [
  /^(about\s*(us|the\s*(company|role|position|team|opportunity))?)\s*[:：]?\s*/i,
  /^(company\s*(description|overview|profile))\s*[:：]?\s*/i,
  /^(job\s*(description|overview|summary))\s*[:：]?\s*/i,
  /^(position\s*(overview|summary))\s*[:：]?\s*/i,
  /^(role\s*(overview|summary|description)?)\s*[:：]?\s*/i,
  /^(key\s*)?(responsibilities|duties)\s*[:：]?\s*/i,
  /^(what\s*you('ll|\s*will)\s*(do|be\s*doing))\s*[:：]?\s*/i,
  /^(requirements|qualifications|skills\s*(&|and)\s*experience)\s*[:：]?\s*/i,
  /^(what\s*we('re|\s*are)\s*looking\s*for)\s*[:：]?\s*/i,
  /^(required|essential|must\s*have)\s*(skills|experience|qualifications)?\s*[:：]?\s*/i,
  /^(desirable|nice\s*to\s*have|preferred)\s*(skills|experience|qualifications)?\s*[:：]?\s*/i,
  /^(benefits|perks|what\s*we\s*offer)\s*[:：]?\s*/i,
  /^(additional\s*information)\s*[:：]?\s*/i,
  /^(salary|compensation|package)\s*[:：]?\s*/i,
];

const JobDescription: React.FC<{ text: string }> = ({ text }) => {
  // Pre-split on common section markers that appear inline without newlines
  const INLINE_SECTION_MARKERS = [
    'Company Description', 'Job Description', 'Position Overview', 'Role Overview',
    'About Us', 'About The Role', 'About The Company', 'About the Position',
    'Key Responsibilities', 'Responsibilities', 'What You Will Do', 'What You\'ll Do',
    'Your Responsibilities', 'Duties',
    'Requirements', 'Qualifications', 'Skills & Experience', 'Skills and Experience',
    'What We\'re Looking For', 'What We Are Looking For', 'Required Skills',
    'Essential Skills', 'Desirable Skills', 'Nice to Have', 'Preferred',
    'Benefits', 'What We Offer', 'Perks', 'Our Benefits',
    'Additional Information', 'Why Join Us', 'Why Version 1',
    'Salary', 'Compensation',
  ];

  let preprocessed = text;
  for (const marker of INLINE_SECTION_MARKERS) {
    const regex = new RegExp(`(?<![\\w])${marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[:：]?\\s*`, 'gi');
    preprocessed = preprocessed.replace(regex, (match) => `\n${match.trim()}\n`);
  }

  const lines = preprocessed.split(/\n/).map(l => l.trim()).filter(Boolean);
  const sections: { heading: string | null; content: string[] }[] = [];
  let current: { heading: string | null; content: string[] } = { heading: null, content: [] };

  for (const line of lines) {
    const isHeading = SECTION_PATTERNS.some(p => p.test(line)) ||
      (line.length < 60 && line.endsWith(':')) ||
      (line === line.toUpperCase() && line.length > 3 && line.length < 60);

    if (isHeading) {
      if (current.content.length > 0 || current.heading) {
        sections.push(current);
      }
      current = { heading: line.replace(/[:：]\s*$/, ''), content: [] };
    } else {
      current.content.push(line);
    }
  }
  if (current.content.length > 0 || current.heading) {
    sections.push(current);
  }

  if (sections.length <= 1) {
    return (
      <div className="text-sm text-gray-700 leading-relaxed">
        {text.split(/\n\n+/).map((para, i) => (
          <p key={i} className="mb-3">{para}</p>
        ))}
      </div>
    );
  }

  return (
    <div className="text-sm text-gray-700 leading-relaxed space-y-4">
      {sections.map((section, i) => (
        <div key={i}>
          {section.heading && (
            <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1.5">{section.heading}</h4>
          )}
          {section.content.map((line, j) => {
            const isBullet = /^[-•–▪■◦]\s/.test(line) || /^\d+[.)]\s/.test(line);
            if (isBullet) {
              return (
                <div key={j} className="flex gap-2 ml-1 mb-1">
                  <span className="text-gray-400 flex-shrink-0">•</span>
                  <span>{line.replace(/^[-•–▪■◦]\s*/, '').replace(/^\d+[.)]\s*/, '')}</span>
                </div>
              );
            }
            return <p key={j} className="mb-2">{line}</p>;
          })}
        </div>
      ))}
    </div>
  );
};

// =====================================================================
// Jobs table (Alex, 21 Oct 2026): one row per vacancy — role, company,
// location, pattern, salary, when it was seen, the source — with View and
// Match on the right and filter chips above. The job spec match flow is
// unchanged behind Match; View opens the description in a panel.
// =====================================================================
// Salary: the feed's fields when set, else the first money range / figure
// the advert itself states ("£45,000 - £55,000", "SEK 55 000 – 65 000 per
// month", "$120k–$140k"). Marked "from the advert" so it's clear it's read,
// not supplied.
const SALARY_RE = /(£|€|\$|USD|GBP|EUR|SEK|NOK|DKK|CHF|AUD|CAD|kr)\s?(\d{2,3}(?:[,. ]\d{3})+|\d{2,3}\s?k)\s*(?:(?:-|–|—|to)\s*(?:£|€|\$|USD|GBP|EUR|SEK|NOK|DKK|CHF|AUD|CAD|kr)?\s?(\d{2,3}(?:[,. ]\d{3})+|\d{2,3}\s?k))?/i;
const salaryFromText = (text: string | null): string | null => {
  if (!text) return null;
  const m = text.match(SALARY_RE);
  if (!m) return null;
  const cur = m[1].length === 1 ? m[1] : `${m[1].toUpperCase()} `;
  const tidy = (v: string) => v.replace(/\s/g, '').toUpperCase();
  return m[3] ? `${cur}${tidy(m[2])} – ${tidy(m[3])}` : `${cur}${tidy(m[2])}`;
};
const formatSalary = (job: Job): { text: string; fromAdvert: boolean } | null => {
  if (job.salary_min || job.salary_max) {
    const cur = job.salary_currency || '';
    const text = job.salary_min && job.salary_max
      ? `${cur}${job.salary_min.toLocaleString()} – ${job.salary_max.toLocaleString()}`
      : `${cur}${(job.salary_min || job.salary_max)?.toLocaleString()}`;
    return { text, fromAdvert: false };
  }
  const fromText = salaryFromText(job.job_description);
  return fromText ? { text: fromText, fromAdvert: true } : null;
};
const cleanDepartment = (d: string | null) => (d && d.startsWith('{') ? d.replace(/[{}"]/g, '') : d);
const jobPlace = (job: Job): string => {
  const parts = [job.city, job.state, job.country].filter(Boolean) as string[];
  return parts.length ? parts.join(', ') : (job.location || '');
};
const jobCountry = (job: Job): string => job.country || (job.location ? job.location.split(',').slice(-1)[0].trim() : '') || 'Unknown';

type JobsSortKey = 'seen' | 'title' | 'company' | 'location' | 'salary';
type SeenWindow = 7 | 30 | 90 | 0;

const JobsTable: React.FC<{
  jobs: Job[];
  projectId: number;
  projectName?: string;
  onDismiss: (jobId: number) => void;
  onNavigateToCompany?: (companyName: string) => void;
  onMatchCandidates?: (job: Job) => void;
  matchBusyJobId: number | null;
  total: number;
  archivedView: boolean;
  onLoadMore?: () => void;
  loadingMore: boolean;
  onArchiveMany: (ids: number[]) => void;
  onRestore: (jobId: number) => void;
}> = ({ jobs, projectId, projectName, onDismiss, onNavigateToCompany, onMatchCandidates, matchBusyJobId, total, archivedView, onLoadMore, loadingMore, onArchiveMany, onRestore }) => {
  const [confirmArchiveAll, setConfirmArchiveAll] = useState(false);
  const { user: authUser } = useAuth();
  // People at the hiring company, in Contact Search — the same screen as a
  // company's "0 hiring managers" click. For an employer that isn't in the
  // project there's no company to open, so this is where its name leads
  // (28 Oct 2026). Looked up by LinkedIn URL, else domain, else name.
  const canFindPeople = !!authUser && (authUser.id === 1 || authUser.role === 'admin' || !!(authUser as any).universeAccess);
  const findPeople = async (job: Job) => {
    // The company the job's employer name really means ("Pepperstone Group"
    // → Pepperstone), resolved on the server and remembered on the job.
    let entry: string | null = job.matched_company_li || job.company_linkedin_url || null;
    let label = job.company_name;
    if (!entry) {
      try {
        const r = await fetch(`/api/sentrion/jobs/${job.id}/employer`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const d = await r.json();
        if (r.ok && d.entry) { entry = d.entry; if (d.name) label = d.name; }
      } catch { /* fall back to the name */ }
    }
    entry = entry || job.company_domain || job.company_name;
    if (!entry) return;
    try {
      sessionStorage.setItem(`jobs-scroll-${projectId}`, String(window.scrollY || document.documentElement.scrollTop || 0));
      sessionStorage.setItem('universe_search_seed', JSON.stringify({
        entries: [entry],
        intent: 'hiring-managers',
        label: label || String(entry),
        review: { projectId, projectName, ...(job.matched_company_id ? { companyId: job.matched_company_id, companyName: job.company_name } : {}) },
      }));
    } catch { /* storage unavailable — the page just opens blank */ }
    window.location.assign('/contact-search');
  };
  const [q, setQ] = useState('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [seen, setSeen] = useState<SeenWindow>(0);
  const [onlyProject, setOnlyProject] = useState(false);
  const [sort, setSort] = useState<{ key: JobsSortKey; dir: 'asc' | 'desc' }>({ key: 'seen', dir: 'desc' });
  const [openJob, setOpenJob] = useState<Job | null>(null);

  const companies = useMemo(() => {
    const n = new Map<string, number>();
    for (const j of jobs) n.set(j.company_name, (n.get(j.company_name) || 0) + 1);
    return Array.from(n.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [jobs]);
  const countries = useMemo(() => {
    const n = new Map<string, number>();
    for (const j of jobs) n.set(jobCountry(j), (n.get(jobCountry(j)) || 0) + 1);
    return Array.from(n.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [jobs]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const cutoff = seen ? Date.now() - seen * 86400000 : 0;
    const list = jobs.filter((j) => {
      if (company && j.company_name !== company) return false;
      if (country && jobCountry(j) !== country) return false;
      if (onlyProject && !j.matched_company_id) return false;
      if (cutoff && new Date(j.first_seen_at).getTime() < cutoff) return false;
      if (needle) {
        const hay = `${j.job_title} ${j.company_name} ${j.location || ''} ${j.department || ''} ${j.seniority_level || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (j: Job): string | number => {
      switch (sort.key) {
        case 'title': return j.job_title.toLowerCase();
        case 'company': return j.company_name.toLowerCase();
        case 'location': return jobPlace(j).toLowerCase();
        case 'salary': { const v = j.salary_max || j.salary_min; if (v) return v; const t = salaryFromText(j.job_description); return t ? parseInt(t.replace(/[^\d]/g, '').slice(0, 7) || '0', 10) : 0; }
        default: return new Date(j.first_seen_at).getTime();
      }
    };
    return list.sort((a, b) => { const x = val(a), y = val(b); return x < y ? -dir : x > y ? dir : 0; });
  }, [jobs, q, company, country, seen, onlyProject, sort]);

  const toggleSort = (key: JobsSortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'seen' || key === 'salary' ? 'desc' : 'asc' }));
  const Th: React.FC<{ k?: JobsSortKey; className?: string; children: React.ReactNode }> = ({ k, className = '', children }) => (
    <th
      className={`px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${k ? 'cursor-pointer select-none hover:text-gray-800' : ''} ${className}`}
      onClick={k ? () => toggleSort(k) : undefined}
    >
      {children}{k && sort.key === k && <span className="ml-1 text-blue-600">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
  const chip = (active: boolean) => `px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`;
  const filtered = rows.length !== jobs.length;
  const goToCompany = (job: Job) => {
    if (!onNavigateToCompany) return;
    try { sessionStorage.setItem(`jobs-scroll-${projectId}`, String(window.scrollY || document.documentElement.scrollTop || 0)); } catch {}
    onNavigateToCompany(job.company_name);
  };

  return (
    <div className="animate-fade-in" data-tour="jobs-table">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search role, company, location…"
          className="input !w-64"
          aria-label="Search vacancies"
        />
        <select value={company} onChange={(e) => setCompany(e.target.value)} className="input !w-auto max-w-[16rem]" aria-label="Company">
          <option value="">All companies ({companies.length})</option>
          {companies.map(([name, n]) => <option key={name} value={name}>{name} ({n})</option>)}
        </select>
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="input !w-auto max-w-[14rem]" aria-label="Country">
          <option value="">All countries</option>
          {countries.map(([name, n]) => <option key={name} value={name}>{name} ({n})</option>)}
        </select>
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-1" role="group" aria-label="Seen in the last">
          {([[7, '7 days'], [30, '30 days'], [90, '90 days'], [0, 'All time']] as const).map(([v, label]) => (
            <button key={v} onClick={() => setSeen(v)} className={chip(seen === v)} title={v ? `Vacancies first seen in the last ${v} days` : 'Every vacancy collected'}>{label}</button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={onlyProject} onChange={(e) => setOnlyProject(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500/30" />
          Companies in this project only
        </label>
        <span className="ml-auto text-xs text-gray-500 flex items-center gap-2 flex-wrap justify-end">
          <span>
            {filtered ? `${rows.length.toLocaleString()} of ${jobs.length.toLocaleString()}` : jobs.length.toLocaleString()}
            {jobs.length < total ? ` loaded of ${total.toLocaleString()}` : ''} {archivedView ? 'archived ' : ''}vacanc{rows.length === 1 && !filtered ? 'y' : 'ies'}
            {filtered && <button className="ml-2 text-blue-600 hover:underline" onClick={() => { setQ(''); setCompany(''); setCountry(''); setSeen(0); setOnlyProject(false); }}>Clear</button>}
          </span>
          {!archivedView && rows.length > 0 && (confirmArchiveAll ? (
            <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 text-amber-900">
              Archive {rows.length.toLocaleString()} shown?
              <button className="font-semibold hover:underline" onClick={() => { onArchiveMany(rows.map((r) => r.id)); setConfirmArchiveAll(false); }}>Archive</button>
              <button className="text-amber-700 hover:underline" onClick={() => setConfirmArchiveAll(false)}>Cancel</button>
            </span>
          ) : (
            <button className="text-gray-600 hover:text-gray-900 hover:underline" onClick={() => setConfirmArchiveAll(true)}
              title="Move every vacancy shown (with the current filters) to the archive. They're kept, and can be restored.">
              Archive all shown
            </button>
          ))}
        </span>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200/80">
              <tr>
                <Th k="title">Role</Th>
                <Th k="company">Company</Th>
                <Th>Hiring managers</Th>
                <Th k="location">Location</Th>
                <Th k="salary" className="text-right">Salary</Th>
                <Th k="seen">Found</Th>
                <Th className="text-right w-px sticky right-0 bg-gray-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)]">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((job) => {
                const salary = formatSalary(job);
                const pattern = [job.job_type, job.is_remote ? 'Remote' : null].filter(Boolean).join(' · ');
                const sub = [cleanDepartment(job.department), job.seniority_level].filter(Boolean).join(' · ');
                return (
                  <tr key={job.id} className="group hover:bg-blue-50/40 transition-colors cursor-pointer" onClick={() => setOpenJob(job)}>
                    <td className="px-4 py-3 align-top max-w-[20rem]">
                      <div className="font-semibold text-gray-900 leading-snug">{job.job_title}</div>
                      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
                      {archivedView && (job as any).archived_reason && (
                        <div className="text-[11px] text-amber-700 mt-0.5">Archived automatically: {(job as any).archived_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top max-w-[16rem]">
                      {!job.matched_company_id && canFindPeople ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); findPeople(job); }} className="font-medium text-blue-700 hover:text-blue-900 hover:underline text-left" title={`Not in your project yet — see who works at ${job.company_name} and pick hiring managers to add`}>
                          {job.company_name}
                        </button>
                      ) : onNavigateToCompany ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); goToCompany(job); }} className="font-medium text-blue-700 hover:text-blue-900 hover:underline text-left" title="Open this company in the Companies tab">
                          {job.company_name}
                        </button>
                      ) : <span className="font-medium text-gray-800">{job.company_name}</span>}
                      <div className="mt-0.5 whitespace-nowrap">
                        {/* "New employer" = not in the project when the job came in
                            (a watch finding hiring managers adds it afterwards). */}
                        {!job.is_new_employer && job.matched_company_id
                          ? <span className="badge badge-blue !text-[10px] !px-1.5">In your project</span>
                          : <span className="badge badge-gray !text-[10px] !px-1.5">New employer</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top max-w-[13rem] text-[11px]">
                        {(job.hms || []).length > 0 ? (
                          <div className="space-y-1.5">
                            {(job.hms || []).slice(0, 2).map((hm) => <HmLine key={hm.id} hm={hm} />)}
                            {(job.hm_count || 0) > 2 && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setOpenJob(job); }} className="text-purple-700 hover:text-purple-900 hover:underline">
                                +{(job.hm_count || 0) - 2} more
                              </button>
                            )}
                          </div>
                        ) : (job.hm_count || 0) > 0 ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); goToCompany(job); }} className="text-purple-700 hover:text-purple-900 hover:underline" title={`Open ${job.company_name} — its hiring managers are on the company row`}>
                            {job.hm_count} hiring manager{job.hm_count === 1 ? '' : 's'}
                          </button>
                        ) : job.watch_finds_hms && !job.hm_discovered && (job.matched_company_id || job.company_linkedin_url || job.company_domain) ? (
                          <span className="text-gray-400 italic" title="This watch finds the hiring managers for each new job — it runs in the background">Finding hiring managers…</span>
                        ) : (
                          <>
                            {job.hm_discovered && <span className="text-gray-400 mr-1.5">None found</span>}
                            {canFindPeople && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); findPeople(job); }} className="text-violet-700 hover:text-violet-900 hover:underline" title={`See who works at ${job.company_name} and pick hiring managers`}>
                                Find people
                              </button>
                            )}
                          </>
                        )}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700 max-w-[13rem]">
                      {jobPlace(job) || <span className="text-gray-400">—</span>}
                      {pattern && <div className="text-xs text-gray-500 mt-0.5">{pattern}</div>}
                    </td>
                    <td className="px-4 py-3 align-top text-right text-gray-700 tabular-nums max-w-[8rem]">
                      {salary
                        ? <span className={salary.fromAdvert ? 'text-gray-600' : ''} title={salary.fromAdvert ? 'Read from the advert text' : 'From the job feed'}>{salary.text}{salary.fromAdvert && <span className="text-gray-400 text-[10px] ml-1">advert</span>}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700 whitespace-nowrap" title={`Found ${new Date(job.first_seen_at).toLocaleString('en-GB')}`}>
                      <div title={[job.watch_name && `Found by the watch "${job.watch_name}"`, job.job_board && `on ${job.job_board}`].filter(Boolean).join(' ')}>
                        {new Date(job.first_seen_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </div>
                      <div className="text-[11px] text-gray-400">{formatRelativeTime(job.first_seen_at)}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-right whitespace-nowrap w-px sticky right-0 bg-white group-hover:bg-blue-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)]">
                      <div className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setOpenJob(job)} className="btn-secondary !px-3 !py-1.5 text-xs">View</button>
                        {onMatchCandidates && job.job_description && job.job_description.length > 100 && (
                          <button
                            onClick={() => onMatchCandidates(job)}
                            disabled={matchBusyJobId === job.id}
                            className="btn-primary !px-3 !py-1.5 text-xs"
                            title="Score this project's candidates against this vacancy"
                          >
                            {matchBusyJobId === job.id ? 'Analysing…' : 'Match'}
                          </button>
                        )}
                        {archivedView ? (
                          <button onClick={() => onRestore(job.id)} className="btn-secondary !px-3 !py-1.5 text-xs" title="Put this vacancy back in the list">Restore</button>
                        ) : (
                          <button onClick={() => onDismiss(job.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" title="Archive: take it off the list (kept — see Archived to restore it)" aria-label="Archive this vacancy">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">No vacancies match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {onLoadMore && (
        <div className="text-center mt-3">
          <button className="btn-secondary text-sm" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Load more (${(total - jobs.length).toLocaleString()} older)`}
          </button>
        </div>
      )}

      {openJob && (
        <JobDetailPanel
          job={openJob}
          onClose={() => setOpenJob(null)}
          onNavigateToCompany={!openJob.matched_company_id && canFindPeople ? () => findPeople(openJob) : onNavigateToCompany ? () => { setOpenJob(null); goToCompany(openJob); } : undefined}
          onFindPeople={canFindPeople ? () => findPeople(openJob) : undefined}
          onMatchCandidates={onMatchCandidates && openJob.job_description && openJob.job_description.length > 100 ? () => onMatchCandidates(openJob) : undefined}
          matchBusy={matchBusyJobId === openJob.id}
          onDismiss={() => { onDismiss(openJob.id); setOpenJob(null); }}
        />
      )}
    </div>
  );
};

// The vacancy in full: description, every detail, the same actions.
const JobDetailPanel: React.FC<{
  job: Job;
  onClose: () => void;
  onNavigateToCompany?: () => void;
  onFindPeople?: () => void;
  onMatchCandidates?: () => void;
  matchBusy: boolean;
  onDismiss: () => void;
}> = ({ job, onClose, onNavigateToCompany, onFindPeople, onMatchCandidates, matchBusy, onDismiss }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const salary = formatSalary(job);
  const facts: [string, string | null][] = [
    ['Location', jobPlace(job) || null],
    ['Pattern', [job.job_type, job.is_remote ? 'Remote' : null].filter(Boolean).join(' · ') || null],
    ['Seniority', job.seniority_level],
    ['Department', cleanDepartment(job.department)],
    ['Salary', salary ? `${salary.text}${salary.fromAdvert ? ' (from the advert)' : ''}` : null],
    ['Posted', job.posted_date ? new Date(job.posted_date).toLocaleDateString() : null],
    ['First seen', new Date(job.first_seen_at).toLocaleString()],
    ['Source', job.job_board],
    ['Watch', job.watch_name],
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-fade-in" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={job.job_title}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {!job.is_new_employer && job.matched_company_id ? <span className="badge badge-blue">In your project</span> : <span className="badge badge-gray">New employer</span>}
              {(job.hm_count || 0) > 0 && <span className="badge badge-purple">{job.hm_count} hiring manager{job.hm_count === 1 ? '' : 's'}</span>}
              {job.is_remote && <span className="badge badge-purple">Remote</span>}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 leading-snug">{job.job_title}</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {onNavigateToCompany
                ? <button type="button" onClick={onNavigateToCompany} className="font-semibold text-blue-700 hover:underline">{job.company_name}</button>
                : <strong>{job.company_name}</strong>}
              {jobPlace(job) && <span className="text-gray-500"> · {jobPlace(job)}</span>}
              {onFindPeople && (
                <button type="button" onClick={onFindPeople} className="ml-2 text-xs text-violet-700 hover:text-violet-900 hover:underline">Find people at {job.company_name} →</button>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          {onMatchCandidates && (
            <button onClick={onMatchCandidates} disabled={matchBusy} className="btn-primary !py-1.5">
              {matchBusy ? 'Analysing…' : 'Match candidates'}
            </button>
          )}
          {job.job_url && (
            <a href={job.job_url} target="_blank" rel="noopener noreferrer" className="btn-secondary !py-1.5">Open the advert ↗</a>
          )}
          <button onClick={onDismiss} className="btn-ghost !py-1.5 ml-auto text-gray-500">Archive</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-5">
            {facts.filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex gap-2 min-w-0">
                <dt className="text-gray-500 w-20 flex-shrink-0">{k}</dt>
                <dd className="text-gray-800 truncate" title={v || ''}>{v}</dd>
              </div>
            ))}
          </dl>
          {(job.hms || []).length > 0 && (
            <div className="mb-5 rounded-xl border border-purple-100 bg-purple-50/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-purple-800 uppercase tracking-wide">Hiring managers at {job.company_name}</h4>
                {onNavigateToCompany && (job.hm_count || 0) > (job.hms || []).length && (
                  <button type="button" onClick={onNavigateToCompany} className="text-xs text-purple-700 hover:underline">All {job.hm_count} →</button>
                )}
              </div>
              <div className="space-y-2.5 text-sm">
                {(job.hms || []).map((hm) => <HmLine key={hm.id} hm={hm} full />)}
              </div>
            </div>
          )}
          {job.job_description
            ? <JobDescription text={job.job_description} />
            : <p className="text-sm text-gray-400 italic">No description available</p>}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Watch card
// =====================================================================
const WatchCard: React.FC<{
  watch: Watch;
  onRunNow: () => void;
  onToggleEnabled: () => void;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ watch, onRunNow, onToggleEnabled, onEdit, onDelete }) => {
  const { user: authUser } = useAuth();
  const titleKeywords = watch.job_keywords?.title?.join(', ');
  const descriptionKeywords = watch.job_keywords?.description?.join(', ');

  return (
    <div className="card p-5 card-hover">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-semibold text-gray-900 tracking-tight">{watch.watch_name}</h3>
            {watch.recurring_enabled ? (
              <span className="badge badge-green">Active</span>
            ) : (
              <span className="badge badge-gray">Paused</span>
            )}
            {watch.status === 'running' && <span className="badge badge-blue">Running</span>}
            {watch.status === 'failed' && <span className="badge badge-red">Failed</span>}
          </div>

          <div className="space-y-1.5 text-sm">
            {titleKeywords && (
              <div className="text-gray-700">
                <span className="text-gray-400">Title keywords:</span> {titleKeywords}
              </div>
            )}
            {descriptionKeywords && (
              <div className="text-gray-700">
                <span className="text-gray-400">Description keywords:</span> {descriptionKeywords}
              </div>
            )}
            {!!watch.departments?.length && (
              <div className="text-gray-700">
                <span className="text-gray-400">Departments:</span> {watch.departments.join(', ')}
              </div>
            )}
            {!!watch.seniority_levels?.length && (
              <div className="text-gray-700">
                <span className="text-gray-400">Seniority:</span> {watch.seniority_levels.join(', ')}
              </div>
            )}
            {!!watch.locations?.length && (
              <div className="text-gray-700">
                <span className="text-gray-400">Locations:</span>{' '}
                {watch.locations.map(l => [l.city, l.state, l.country].filter(Boolean).join(', ')).join(' · ')}
              </div>
            )}
            {watch.scope_to_project_companies && (
              <div className="text-xs text-purple-700 bg-purple-50 inline-block px-2 py-0.5 rounded"
                title="Each run checks the companies it checked longest ago, so every company in the project is covered in turn. Checks that find nothing cost nothing.">
                Scoped to project companies only
                {!!watch.scope_companies_total && (
                  <> · checks all {watch.scope_companies_total.toLocaleString()} companies
                    {watch.scope_sweep_days ? `, every ${watch.scope_sweep_days <= 1 ? 'run' : `~${Math.round(watch.scope_sweep_days)} days`}` : ''}</>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
            <span>{watch.jobs_count} jobs</span>
            <span>· {watch.recurring_frequency}</span>
            <span>· Next run: {formatNextRun(watch.next_run_at)}</span>
            <span>· Last run: {formatRelativeTime(watch.last_recurring_run_at)}</span>
            {watch.auto_discover_hms && <span className="text-purple-700">· Finds hiring managers</span>}
            {/* Our credits with the job data supplier — Admin only: users read it as their own credits. */}
            {authUser?.role === 'admin' && watch.total_credits_used > 0 && <span>· {watch.total_credits_used} supplier credits used</span>}
          </div>

          {watch.error_message && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
              Error: {watch.error_message}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onRunNow}
            disabled={watch.status === 'running'}
            className="btn-ghost text-xs"
            title="Run now"
          >
            Run now
          </button>
          <button
            onClick={onToggleEnabled}
            className="btn-ghost text-xs"
          >
            {watch.recurring_enabled ? 'Pause' : 'Resume'}
          </button>
          <button onClick={onEdit} className="btn-ghost text-xs">Edit</button>
          <button onClick={onDelete} className="btn-ghost text-xs text-red-600 hover:bg-red-50">Delete</button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Watch editor modal
// =====================================================================
const WatchEditorModal: React.FC<{
  projectId: number;
  watch: Watch | null;
  reference: ReferenceData;
  onClose: () => void;
  onSaved: () => void;
}> = ({ projectId, watch, reference, onClose, onSaved }) => {
  const [name, setName] = useState(watch?.watch_name || '');
  const [titleKeywords, setTitleKeywords] = useState(watch?.job_keywords?.title?.join(', ') || '');
  const [descriptionKeywords, setDescriptionKeywords] = useState(watch?.job_keywords?.description?.join(', ') || '');
  const [excludeKeywords, setExcludeKeywords] = useState(watch?.exclude_job_keywords?.title?.join(', ') || '');
  const [excludeDescriptionKeywords, setExcludeDescriptionKeywords] = useState(watch?.exclude_job_keywords?.description?.join(', ') || '');
  const [keywordsMode, setKeywordsMode] = useState<'and' | 'or'>(watch?.job_keywords?.mode || 'or');
  const [departments, setDepartments] = useState<string[]>(watch?.departments || []);
  const [seniorityLevels, setSeniorityLevels] = useState<string[]>(watch?.seniority_levels || []);
  const [jobTypes, setJobTypes] = useState<string[]>(watch?.job_types || []);
  const [locations, setLocations] = useState<Location[]>(watch?.locations || []);
  const [scopeToProjectCompanies, setScopeToProjectCompanies] = useState(watch?.scope_to_project_companies || false);
  const [excludeAgency, setExcludeAgency] = useState(watch?.exclude_agency ?? true);
  const [autoDiscoverHms, setAutoDiscoverHms] = useState(!!watch?.auto_discover_hms);
  const [maxJobAgeDays, setMaxJobAgeDays] = useState<number>((watch as any)?.max_job_age_days || 30);
  const [recurringFrequency, setRecurringFrequency] = useState(watch?.recurring_frequency || 'daily');
  const [recurringEnabled, setRecurringEnabled] = useState(watch?.recurring_enabled ?? true);

  // Watch-level company pre-filters. Only applied when scopeToProjectCompanies
  // is on (Mode A — per-company search).
  const [companyEmployeeMin, setCompanyEmployeeMin] = useState<string>(
    watch?.company_employee_min != null ? String(watch.company_employee_min) : ''
  );
  const [companyEmployeeMax, setCompanyEmployeeMax] = useState<string>(
    watch?.company_employee_max != null ? String(watch.company_employee_max) : ''
  );
  const [companyCountries, setCompanyCountries] = useState<string>(
    (watch?.company_countries || []).join(', ')
  );
  const [companyIndustries, setCompanyIndustries] = useState<string>(
    (watch?.company_industries || []).join(', ')
  );
  const [companyTags, setCompanyTags] = useState<string>(
    (watch?.company_tags || []).join(', ')
  );

  // Single location entry form
  const [newCountry, setNewCountry] = useState('');
  const [newState, setNewState] = useState('');
  const [newCity, setNewCity] = useState('');

  const [saving, setSaving] = useState(false);
  // No location = jobs from every country: shown under Locations, and the
  // first Save asks once more (29 Oct 2026).
  const [worldwideOk, setWorldwideOk] = useState(false);
  const [error, setError] = useState('');

  const toggleArrayItem = (arr: string[], item: string, setter: (v: string[]) => void) => {
    setter(arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);
  };

  const addLocation = () => {
    if (!newCountry && !newState && !newCity) return;
    setLocations([...locations, {
      ...(newCountry && { country: newCountry }),
      ...(newState && { state: newState }),
      ...(newCity && { city: newCity })
    }]);
    setNewCountry(''); setNewState(''); setNewCity('');
  };

  const removeLocation = (i: number) => {
    setLocations(locations.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Watch name is required'); return; }
    if (!locations.length && !newCountry && !newState && !newCity && !worldwideOk) {
      setWorldwideOk(true);
      return;
    }
    setSaving(true);
    setError('');

    // Auto-add any unsaved location text
    const finalLocations = [...locations];
    if (newCountry || newState || newCity) {
      finalLocations.push({
        ...(newCountry && { country: newCountry }),
        ...(newState && { state: newState }),
        ...(newCity && { city: newCity })
      });
      setNewCountry(''); setNewState(''); setNewCity('');
      setLocations(finalLocations);
    }

    // People type "C# OR asp.net OR .net" as often as commas — every one of
    // those separators splits into separate terms (28 Oct 2026: a watch saved
    // "C# OR asp.net OR .net OR .netcore" as ONE phrase and found nothing).
    const splitTerms = (s: string): string[] =>
      s.split(/\s+or\s+|\s*[,;|]\s*/i).map(x => x.trim().replace(/^[\s"'“”‘’()[\]{}]+|[\s"'“”‘’()[\]{}]+$/g, '')).filter(Boolean);
    const titleArr = splitTerms(titleKeywords);
    const descArr = splitTerms(descriptionKeywords);
    const excludeArr = splitTerms(excludeKeywords);
    const excludeDescArr = splitTerms(excludeDescriptionKeywords);

    const parseCsvList = (s: string): string[] =>
      s.split(',').map(x => x.trim()).filter(Boolean);
    const parseInt0OrNull = (s: string): number | null => {
      const trimmed = s.trim();
      if (!trimmed) return null;
      const n = parseInt(trimmed, 10);
      return Number.isFinite(n) && n >= 0 ? n : null;
    };

    const body: any = {
      name: name.trim(),
      jobKeywords: (titleArr.length || descArr.length) ? {
        ...(titleArr.length && { title: titleArr }),
        ...(descArr.length && { description: descArr }),
        // Title AND description whenever both are given (3 Nov 2026).
        mode: (titleArr.length && descArr.length) ? 'and' : keywordsMode
      } : null,
      excludeJobKeywords: (excludeArr.length || excludeDescArr.length) ? {
        ...(excludeArr.length && { title: excludeArr }),
        ...(excludeDescArr.length && { description: excludeDescArr }),
        mode: 'or'
      } : null,
      departments,
      seniorityLevels,
      jobTypes,
      locations: finalLocations,
      scopeToProjectCompanies,
      excludeAgency,
      autoDiscoverHms,
      maxJobAgeDays,
      recurringFrequency,
      recurringEnabled,
      // Watch-level company pre-filters (only meaningful when
      // scopeToProjectCompanies is on, but always sent so they persist)
      companyEmployeeMin: parseInt0OrNull(companyEmployeeMin),
      companyEmployeeMax: parseInt0OrNull(companyEmployeeMax),
      companyCountries: parseCsvList(companyCountries),
      companyIndustries: parseCsvList(companyIndustries),
      companyTags: parseCsvList(companyTags)
    };

    try {
      const url = watch
        ? `/api/sentrion/watches/${watch.id}`
        : `/api/sentrion/projects/${projectId}/watches`;
      const method = watch ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save watch');
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save watch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-dialog max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200/80 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
            {watch ? 'Edit Job Watch' : 'New Job Watch'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200/60 rounded-xl p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Watch Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. SAP Consultants - Nordics"
              className="input"
            />
          </div>

          {/* Keywords */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">Job Title Keywords</label>
            </div>
            <input
              type="text"
              value={titleKeywords}
              onChange={e => setTitleKeywords(e.target.value)}
              placeholder="e.g. SAP S/4HANA, FICO Consultant (commas or OR between them)"
              className="input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Matched against the <strong>job title</strong> only. Separate with commas.
            </p>
          </div>

          {/* Description keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description Keywords</label>
            <input
              type="text"
              value={descriptionKeywords}
              onChange={e => {
                setDescriptionKeywords(e.target.value);
              }}
              placeholder="e.g. LNG, offshore wind, dry dock (commas or OR between them)"
              className="input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Matched against the <strong>job description</strong> — the right home for sector and market
              terms. A job must match <em>at least one</em> title keyword <em>and</em> mention <em>at least
              one</em> of these.
            </p>
          </div>

          {/* Exclude keywords */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Exclude Title Keywords</label>
              <input
                type="text"
                value={excludeKeywords}
                onChange={e => setExcludeKeywords(e.target.value)}
                placeholder="e.g. Intern, Junior"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Exclude Description Keywords</label>
              <input
                type="text"
                value={excludeDescriptionKeywords}
                onChange={e => setExcludeDescriptionKeywords(e.target.value)}
                placeholder="e.g. agency, freelance"
                className="input"
              />
            </div>
          </div>

          {/* Departments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Departments</label>
            <div className="flex flex-wrap gap-1.5">
              {reference.departments.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleArrayItem(departments, d, setDepartments)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 ${
                    departments.includes(d)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Seniority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Seniority Level</label>
            <div className="flex flex-wrap gap-1.5">
              {reference.seniorityLevels.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleArrayItem(seniorityLevels, s, setSeniorityLevels)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 ${
                    seniorityLevels.includes(s)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Job types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
            <div className="flex flex-wrap gap-1.5">
              {reference.jobTypes.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleArrayItem(jobTypes, t, setJobTypes)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 ${
                    jobTypes.includes(t)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Locations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Locations</label>
            {locations.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {locations.map((loc, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-700">
                      {[loc.city, loc.state, loc.country].filter(Boolean).join(', ')}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeLocation(i)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!locations.length && !newCountry && !newState && !newCity && (
              <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <strong>No location yet:</strong> this watch would bring in jobs from every country{scopeToProjectCompanies ? ' at this project\'s companies' : ''}, which can use up your job vacancy allowance quickly. Add at least a country, e.g. United Kingdom.
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={newCity} onChange={e => setNewCity(e.target.value)} placeholder="City" className="input" />
              <input type="text" value={newState} onChange={e => setNewState(e.target.value)} placeholder="State / Region" className="input" />
              <input type="text" value={newCountry} onChange={e => setNewCountry(e.target.value)} placeholder="Country" className="input" />
            </div>
            <button
              type="button"
              onClick={addLocation}
              className="btn-secondary text-xs mt-2"
            >
              + Add location
            </button>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={scopeToProjectCompanies}
                onChange={e => setScopeToProjectCompanies(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Only show jobs from companies in this project</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Restricts results to jobs at companies you've already mapped. Slower (calls API per company) but more relevant.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={excludeAgency}
                onChange={e => setExcludeAgency(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Exclude recruitment agency listings</div>
                <div className="text-xs text-gray-500 mt-0.5">Filter out jobs posted by recruitment agencies and outsourcing firms.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={autoDiscoverHms}
                onChange={e => setAutoDiscoverHms(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Find the hiring managers for each new job <span className="ml-1 text-[10px] font-semibold text-green-700 bg-green-50 rounded-full px-1.5 py-0.5 align-middle">free</span></div>
                <div className="text-xs text-gray-500 mt-0.5">When a job comes in, we look for the person who'd manage that hire (and who runs recruitment there) at the company, in the job's country, and add them to your Hiring Managers. New employers are added to the project first. Each company once.</div>
              </div>
            </label>
            <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div>
                <div className="text-sm font-medium text-gray-900">Ignore jobs older than</div>
                <div className="text-xs text-gray-500 mt-0.5">Postings past this age are too stale to chase and are skipped.</div>
              </div>
              <select
                value={maxJobAgeDays}
                onChange={e => setMaxJobAgeDays(parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
          </div>

          {/* Company pre-filters — only meaningful when scoped to project
              companies (Mode A). Hidden otherwise to avoid implying the
              filter does something it doesn't. */}
          {scopeToProjectCompanies && (
            <div className="border-t border-gray-200/80 pt-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 tracking-tight">Narrow which companies to check</h3>
                <p className="text-xs text-gray-500 mt-1">
                  All filters are optional. Companies must match every filter you set.
                  Cuts wasted API calls (and stretches your monthly job-vacancy allowance) by
                  skipping companies you'd never pursue.
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Employee count</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <input
                    type="number"
                    min={0}
                    placeholder="Min (e.g. 50)"
                    value={companyEmployeeMin}
                    onChange={e => setCompanyEmployeeMin(e.target.value)}
                    className="input"
                  />
                  <input
                    type="number"
                    min={0}
                    placeholder="Max (e.g. 500)"
                    value={companyEmployeeMax}
                    onChange={e => setCompanyEmployeeMax(e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Countries</label>
                <input
                  type="text"
                  placeholder="Comma-separated, e.g. UK, Sweden, Denmark"
                  value={companyCountries}
                  onChange={e => setCompanyCountries(e.target.value)}
                  className="input mt-1"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Industries</label>
                <input
                  type="text"
                  placeholder="Comma-separated, e.g. Software, Fintech"
                  value={companyIndustries}
                  onChange={e => setCompanyIndustries(e.target.value)}
                  className="input mt-1"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Tags</label>
                <input
                  type="text"
                  placeholder="Comma-separated, matches any tag"
                  value={companyTags}
                  onChange={e => setCompanyTags(e.target.value)}
                  className="input mt-1"
                />
              </div>
            </div>
          )}

          {/* Schedule */}
          <div className="border-t border-gray-200/80 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 tracking-tight mb-3">Schedule</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Run frequency</label>
                <select
                  value={recurringFrequency}
                  onChange={e => setRecurringFrequency(e.target.value)}
                  className="input"
                >
                  {reference.recurringFrequencies.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-3 cursor-pointer pt-7">
                <input
                  type="checkbox"
                  checked={recurringEnabled}
                  onChange={e => setRecurringEnabled(e.target.checked)}
                />
                <span className="text-sm font-medium text-gray-900">Enable recurring runs</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200/80 flex items-center justify-end gap-2 bg-gray-50/50 flex-wrap">
          {worldwideOk && !locations.length && !newCountry && !newState && !newCity && (
            <span className="mr-auto text-xs text-amber-700 basis-full sm:basis-auto">
              No location: this watch will find jobs in every country. Add one above, or save to watch worldwide.
            </span>
          )}
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : worldwideOk && !locations.length && !newCountry && !newState && !newCity
              ? 'Save — every country'
              : (watch ? 'Save Changes' : 'Create Watch')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectJobWatches;
